import { DragEvent, useCallback } from 'react';
import {
  Content,
  Icon,
  Inline,
  InlineItem,
  Pill,
  Radio,
  RadioGroup,
  Stack,
  Strong,
  Text,
  UtilityButton,
} from '@kinesis/bungle';
import type { InstalledEfLibrary } from '@/types';

// Bungle RadioGroup uses string values internally.
const LATEST = 'latest';
const PINNED = 'pinned';

// One row in the scenario's emission factor library list — a port of suit's
// `library-row`, shifted to village's `InstalledEfLibrary` shape. Supports:
//
//   - Optional drag-reorder: the parent owns "which index is being dragged"
//     state; the row fires onDragStart / onDragOver / onDrop.
//   - Optional pin control: when `onPinChange` is present the row renders a
//     radio pair ("Latest" ↔ "Pin to current version"). When pinned to a
//     version older than the library's latest published version, an "Update
//     available" badge appears.
//   - Optional remove button: when `onRemove` is present.
//
// With none of the editable props the row renders read-only (viewer mode).
type LibraryRowProps = {
  library: InstalledEfLibrary;
  index: number;
  draggable?: boolean;
  onDragStart?: (index: number) => void;
  onDragOver?: (index: number) => void;
  onDrop?: (index: number) => void;
  // When present the row is editable for pin state. The handler receives the
  // library id and the desired pin state (so the parent can resolve the version
  // to pin to).
  onPinChange?: (libraryId: number, pinned: boolean) => void;
  // When present the row exposes a delete button that fires with the library id.
  onRemove?: (libraryId: number) => void;
};

const LibraryRow = ({
  library,
  index,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  onPinChange,
  onRemove,
}: LibraryRowProps) => {
  const handleDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      // Required for Firefox to actually start the drag.
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', String(index));
      onDragStart?.(index);
    },
    [index, onDragStart],
  );

  const handleDragOver = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      // Without preventing default the drop event never fires.
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      onDragOver?.(index);
    },
    [index, onDragOver],
  );

  const handleDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      onDrop?.(index);
    },
    [index, onDrop],
  );

  const handlePinChange = useCallback(
    (value: string | number | undefined) => {
      onPinChange?.(library.efLibrary, value === PINNED);
    },
    [library.efLibrary, onPinChange],
  );

  const handleRemove = useCallback(() => {
    onRemove?.(library.efLibrary);
  }, [library.efLibrary, onRemove]);

  const pinned = library.version != null;
  // An "update available" badge appears only when the user is intentionally on
  // an older version — i.e. pinned and the library has since published a newer
  // version. A null `latestVersion` means the library has never been published
  // (no comparison possible, no badge).
  const hasNewerVersion =
    pinned &&
    library.latestVersion != null &&
    library.version != null &&
    library.latestVersion > library.version;

  const editablePin = typeof onPinChange === 'function';

  return (
    <div
      draggable={draggable}
      onDragStart={draggable ? handleDragStart : undefined}
      onDragOver={draggable ? handleDragOver : undefined}
      onDrop={draggable ? handleDrop : undefined}
      aria-label={`Library row ${library.name}`}
    >
      <Content background='white' border padding='small' paddingMode='equal'>
        <Inline space='small'>
          {draggable && (
            <InlineItem sizing='fit-content'>
              <div
                aria-hidden='true'
                style={{
                  cursor: 'grab',
                  color: '#9b9b9b',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Icon type='drag' magnitude='medium' />
              </div>
            </InlineItem>
          )}
          <InlineItem sizing='fill-container'>
            <Stack space='small'>
              <Inline space='small'>
                <InlineItem sizing='fill-container'>
                  <Text>
                    <Strong>{library.name}</Strong>
                  </Text>
                </InlineItem>
                {hasNewerVersion && (
                  <InlineItem sizing='fit-content'>
                    <Pill>Update available</Pill>
                  </InlineItem>
                )}
                {onRemove && (
                  <InlineItem sizing='fit-content'>
                    <UtilityButton
                      aria-label={`Remove ${library.name}`}
                      icon='delete'
                      variant='danger'
                      onClick={handleRemove}
                      magnitude='small'
                    />
                  </InlineItem>
                )}
              </Inline>
              {editablePin ? (
                <RadioGroup
                  hideLegend
                  legend={`Version pin for ${library.name}`}
                  value={pinned ? PINNED : LATEST}
                  onChange={handlePinChange}
                >
                  <Radio value={LATEST} title='Latest'>
                    Follow the library’s latest published version.
                  </Radio>
                  <Radio
                    value={PINNED}
                    title={
                      pinned && library.version != null
                        ? `Pinned to v${library.version}`
                        : 'Pin to current version'
                    }
                  >
                    Freeze this library at the version live today. Useful for
                    locked disclosures.
                  </Radio>
                </RadioGroup>
              ) : pinned ? (
                <Pill>Pinned · v{library.version}</Pill>
              ) : (
                <Pill>Latest</Pill>
              )}
            </Stack>
          </InlineItem>
        </Inline>
      </Content>
    </div>
  );
};

export { LibraryRow };
export type { LibraryRowProps };
